/**
 * Reading module interface component.
 *
 * Main interface for reading module with text panel and question panel.
 *
 * @module ReadingInterface
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useReadingModuleState } from "@/hooks/useReadingModuleState";
import { useAuth } from "@/hooks/useAuth";
import { ExamHeader } from "./ExamHeader";
import { TextPanel } from "./TextPanel";
import { QuestionPanel } from "./QuestionPanel";
import {
  ResizableModulePanels,
  SimulationModuleError,
  SimulationModuleLoading,
  SimulationSavingOverlay,
} from "./shared";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import {
  buildMcqNavigatorItems,
  filterUnansweredItems,
} from "@/utils/simulationQuestionNavigation";
import { SimulationUnansweredJumpChips } from "@/components/exam/SimulationUnansweredJumpChips";
import { useSimulationInactivityMonitor } from "@/hooks/useSimulationInactivityMonitor";
import { SIMULATION_INACTIVITY_TIMEOUT_MS } from "@/lib/simulationRuntimeConfig";
import { SimulationInactivityDialog } from "@/components/exam/SimulationInactivityDialog";
import { formatSimulationSectionTitle } from "@/utils/simulationSectionTitle";
import {
  incompleteModuleDialogTitle,
  submitModuleButtonLabel,
} from "@/lib/simulationSubmitModuleLabels";
import { useIsLargeScreen } from "@/hooks/use-large-screen";
import type {
  ExamType,
  CEFRLevel,
  SimulationSettings,
} from "@/types/exam-simulation";

interface ReadingInterfaceProps {
  readonly examType: ExamType;
  readonly level: CEFRLevel;
  readonly settings: SimulationSettings | null;
  readonly onModuleFinish?: () => void;
  /** Session ID shared across all modules in a simulation */
  readonly sessionId?: string | null;
}

export function ReadingInterface({
  examType,
  level,
  settings,
  onModuleFinish,
  sessionId,
}: ReadingInterfaceProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const {
    state,
    currentChunk,
    currentQuestions,
    highlightedParagraphId,
    totalChunks,
    totalQuestions,
    allQuestions,
    isLoadingData,
    dataError,
    setActiveQuestion,
    jumpToQuestion,
    selectAnswer,
    toggleStrikeAnswer,
    toggleFlagQuestion,
    navigateChunk,
    setTextSize,
    toggleHighlighter,
    eraseHighlights,
    addHighlight,
    saveAnswersAndResult,
  } = useReadingModuleState({
    examType,
    level,
    settings,
    userId: user?.id || null,
    sessionId,
  });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasTimerExpired, setHasTimerExpired] = useState(false);
  const [activeTab, setActiveTab] = useState<"text" | "questions">("text");
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const isLargeScreen = useIsLargeScreen();
  const inactivity = useSimulationInactivityMonitor({
    enabled: !isSaving && !hasTimerExpired && allQuestions.length > 0,
    timeoutMs: SIMULATION_INACTIVITY_TIMEOUT_MS,
  });

  const mcqNavigatorItems = useMemo(
    () =>
      allQuestions.length > 0
        ? buildMcqNavigatorItems(
            allQuestions,
            state.selectedAnswers,
            state.flaggedQuestions,
          )
        : [],
    [allQuestions, state.selectedAnswers, state.flaggedQuestions],
  );

  const unansweredNavItems = useMemo(
    () => filterUnansweredItems(mcqNavigatorItems),
    [mcqNavigatorItems],
  );

  const questionNavigator = useMemo(
    () =>
      mcqNavigatorItems.length > 0
        ? {
            items: mcqNavigatorItems,
            onJump: (id: string) => {
              jumpToQuestion(Number(id));
              setActiveTab("questions");
            },
          }
        : null,
    [mcqNavigatorItems, jumpToQuestion],
  );

  /**
   * Save answers and result, then proceed to finish
   */
  const handleSaveAndFinish = useCallback(async () => {
    if (isSaving || allQuestions.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      await saveAnswersAndResult(allQuestions, state.selectedAnswers);

      if (onModuleFinish) {
        onModuleFinish();
      }
    } catch (error) {
      logger.error("Error saving answers", error);
      toast.error(
        t("simulation.errors.saveFailed", {
          defaultValue:
            "Failed to save your answers. Please try again or contact support if the problem persists.",
        }),
      );
      setIsSaving(false);
      setHasTimerExpired(false);
    }
  }, [
    allQuestions,
    state.selectedAnswers,
    saveAnswersAndResult,
    isSaving,
    onModuleFinish,
    t,
  ]);

  // Handle timer expiration
  useEffect(() => {
    if (
      state.timeRemaining === 0 &&
      !hasTimerExpired &&
      !isSaving &&
      allQuestions.length > 0
    ) {
      setHasTimerExpired(true);
      handleSaveAndFinish();
    }
  }, [
    state.timeRemaining,
    hasTimerExpired,
    isSaving,
    allQuestions.length,
    handleSaveAndFinish,
  ]);

  const handleFinish = () => {
    if (isSaving) {
      return; // Prevent multiple clicks while saving
    }

    if (unansweredNavItems.length > 0) {
      // Show confirmation dialog
      setShowConfirmDialog(true);
    } else {
      // All questions answered, save and finish
      handleSaveAndFinish();
    }
  };

  const handleConfirmFinish = () => {
    setShowConfirmDialog(false);
    // Save and finish (includes unanswered questions)
    handleSaveAndFinish();
  };

  const handleReviewUnanswered = () => {
    setShowConfirmDialog(false);
    const firstUnanswered = unansweredNavItems[0];
    if (firstUnanswered) {
      jumpToQuestion(Number(firstUnanswered.id));
      setActiveTab("questions");
    }
  };

  const moduleDisplayName = t("simulation.instructions.modules.reading", {
    defaultValue: "Reading",
  });
  const sectionTitle = formatSimulationSectionTitle(
    examType,
    level,
    moduleDisplayName,
  );

  if (isLoadingData) {
    return (
      <SimulationModuleLoading
        message={t("simulation.errors.loadingModule", {
          module: "Reading",
          defaultValue: "Loading reading module data...",
        })}
      />
    );
  }

  if (dataError || !currentChunk) {
    return (
      <SimulationModuleError
        destructiveBorder
        message={
          dataError instanceof Error
            ? dataError.message
            : t("simulation.errors.loadFailure", {
                moduleName: "Reading",
                attempts: 1,
                errorMessage: "Unknown",
                defaultValue: "Failed to load reading module data",
              })
        }
        hint={t("simulation.errors.tryAgain", {
          defaultValue: "Please go back and try again.",
        })}
      />
    );
  }

  const textPanel = (
    <TextPanel
      chunk={currentChunk}
      currentChunkIndex={state.currentChunkIndex}
      totalChunks={totalChunks}
      highlightedParagraphId={highlightedParagraphId}
      textSize={state.textSize}
      highlighterActive={state.highlighterActive}
      questions={currentQuestions}
      examType={examType}
      level={level}
      highlights={state.highlights}
      onNavigate={navigateChunk}
      onTextSizeChange={setTextSize}
      onHighlighterToggle={toggleHighlighter}
      onEraseHighlights={eraseHighlights}
      onAddHighlight={addHighlight}
      activeQuestionId={state.activeQuestionId}
      onContextQuestionSelect={(questionId) => {
        setActiveQuestion(questionId);
        setScrollTrigger((t) => t + 1);
        setActiveTab("questions");
      }}
    />
  );

  const questionPanel = (
    <QuestionPanel
      questions={currentQuestions}
      activeQuestionId={state.activeQuestionId}
      selectedAnswers={state.selectedAnswers}
      struckAnswers={state.struckAnswers}
      flaggedQuestions={state.flaggedQuestions}
      totalQuestions={totalQuestions}
      currentIndex={state.currentChunkIndex}
      scrollTrigger={scrollTrigger}
      onQuestionSelect={(questionId) => {
        setActiveQuestion(questionId);
        setScrollTrigger((t) => t + 1);
      }}
      onAnswerSelect={selectAnswer}
      onStrikeToggle={toggleStrikeAnswer}
      onFlagToggle={toggleFlagQuestion}
      questionNavigator={questionNavigator}
    />
  );

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background">
      {/* Global Header */}
      <ExamHeader
        sectionTitle={sectionTitle}
        currentChunk={state.currentChunkIndex}
        totalChunks={totalChunks}
        timeRemaining={state.timeRemaining}
        onFinish={handleFinish}
        disabled={isSaving || hasTimerExpired}
        chunkLabel={t("simulation.active.chunkLabel.passage", {
          defaultValue: "Passage",
        })}
        finishModule="reading"
      />

      {/* Main Content: Responsive Layout */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Mobile View Toggle (Tabs) */}
        <div className="lg:hidden flex border-b bg-muted/30 shrink-0">
          <button
            className={cn(
              "flex-1 py-3 text-sm font-semibold transition-colors border-b-2",
              activeTab === "text"
                ? "border-primary text-primary bg-background"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => setActiveTab("text")}
          >
            {t("simulation.active.passage", {
              defaultValue: "Reading Passage",
            })}
          </button>
          <button
            className={cn(
              "flex-1 py-3 text-sm font-semibold transition-colors border-b-2 relative",
              activeTab === "questions"
                ? "border-primary text-primary bg-background"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => setActiveTab("questions")}
          >
            {t("simulation.active.questions", { defaultValue: "Questions" })}
            {Object.keys(state.selectedAnswers).length < totalQuestions && (
              <span className="absolute top-2 right-4 w-2 h-2 bg-orange-500 rounded-full" />
            )}
          </button>
        </div>

        {isLargeScreen ? (
          <ResizableModulePanels
            autoSaveId="simulation-reading-horizontal-panels"
            left={textPanel}
            right={questionPanel}
            leftDefaultSize={60}
            rightDefaultSize={40}
            leftMinSize={35}
            rightMinSize={30}
          />
        ) : (
          <>
            <div
              className={cn(
                activeTab === "text"
                  ? "flex-1 flex flex-col overflow-hidden"
                  : "hidden",
              )}
            >
              {textPanel}
            </div>
            <div
              className={cn(
                activeTab === "questions"
                  ? "flex-1 flex flex-col overflow-hidden border-none"
                  : "hidden",
              )}
            >
              {questionPanel}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {incompleteModuleDialogTitle(t, "reading")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("simulation.active.unansweredQuestionsConsequence", {
                count: unansweredNavItems.length,
                module: moduleDisplayName,
                defaultValue:
                  "These {{count}} unanswered responses will be marked as not answered and counted against your score. Are you sure you want to submit the {{module}} module?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <SimulationUnansweredJumpChips
            items={unansweredNavItems}
            disabled={isSaving}
            onJump={(id) => {
              setShowConfirmDialog(false);
              jumpToQuestion(Number(id));
              setActiveTab("questions");
            }}
          />
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleReviewUnanswered}
              disabled={isSaving}
            >
              {t("simulation.active.reviewQuestions", {
                defaultValue: "Review Questions",
              })}
            </AlertDialogAction>
            <AlertDialogAction
              className="border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={handleConfirmFinish}
              disabled={isSaving}
            >
              {isSaving
                ? t("common.saving", { defaultValue: "Saving..." })
                : submitModuleButtonLabel(t, "reading")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SimulationSavingOverlay open={isSaving} />

      <SimulationInactivityDialog
        open={inactivity.isPromptOpen}
        timeRemainingSeconds={state.timeRemaining}
        onContinue={inactivity.continueSession}
        onEnd={handleSaveAndFinish}
      />
    </div>
  );
}
